import React, { useState } from "react";
import type { CalendarEvent } from "../../../types/types";
import { eventFormSchema } from "./eventFormSchema";
import { useAddEvent } from "../hooks/useAddEvent";
import { useEditEvent } from "../hooks/useEditEvent";
import { Button } from "../../ui/button";

interface EventFormProps {
  initial?: Partial<CalendarEvent>;
  selectedDate?: Date;
  onCancel: () => void;
  onSubmit?: (event: CalendarEvent) => void; 
}

export const EventForm: React.FC<EventFormProps> = ({
  initial,
  selectedDate,
  onCancel,
}) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [source, setSource] = useState<"manual" | "meta" | "clayrio">(initial?.source || "manual");
  const [channel, setChannel] = useState<"ecommerce" | "retail" | "wholesale" | "">(initial?.channel ?? "");
  const [location, setLocation] = useState(initial?.location || "");
  const [startAt, setStartAt] = useState(initial?.startAt || (selectedDate ? selectedDate.toISOString().slice(0, 10) : ""));
  const [endAt, setEndAt] = useState(initial?.endAt || (selectedDate ? selectedDate.toISOString().slice(0, 10) : ""));
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [message, setMessage] = useState<string | null>(null);

  const { mutate: addEvent, isPending: isAdding } = useAddEvent();
  const { mutate: editEvent, isPending: isEditing } = useEditEvent();

  const isEditingMode = !!initial?.id;

  function validate() {
    const result = eventFormSchema.safeParse({
      title,
      description,
      source,
      channel,
      location,
      startAt,
      endAt,
    });
    if (!result.success) {
      const fieldErrors: { [key: string]: string } = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) fieldErrors[err.path[0]] = err.message;
      });
      setErrors(fieldErrors);
      setMessage("Please fix the errors below.");
      return false;
    }

    // additional validation for retail channel
    if (channel === "retail" && !location?.trim()) {
      setErrors({ location: "Location is required for retail events." });
      setMessage("Please fix the errors below.");
      return false;
    }

    setErrors({});
    setMessage(null);
    return true;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      title,
      description,
      source,
      channel,
      start_at: startAt,
      end_at: endAt,
      ...(channel === "retail" ? { location } : {}),
    };

    
    
    if (isEditingMode) {
      // Edit existing event
      editEvent(
        { keypointId: initial!.id!, payload },
        {
          onSuccess: () => {
            onCancel(); // Close the form on success
          },
          onError: (error) => {
            console.error("Failed to edit event:", error);
          },
        }
      );
    } else {
      // Add new event
      addEvent(payload, {
        onSuccess: () => {
          onCancel(); // Close the form on success
        },
        onError: (error) => {
          console.error("Failed to add event:", error);
        },
      });
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-card text-card-foreground border border-border rounded shadow w-[340px] p-6">
        <h4 className="font-bold mb-4 text-lg">{isEditingMode ? "Edit Event" : "Add Event"}</h4>
        <form onSubmit={handleSubmit} className="space-y-4">
          {message && (
            <div className="p-2 rounded text-sm bg-red-100 text-red-800 mb-2">
              {message}
            </div>
          )}
          <div>
            <label htmlFor="title" className="block text-sm font-medium mb-1">
              Title
            </label>
            <input
              id="title"
              className="bg-input text-foreground border border-border rounded p-2 w-full"
              placeholder="Enter event title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
            {errors.title && (
              <span className="text-xs text-red-600">{errors.title}</span>
            )}
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-1">
              Description
            </label>
            <input
              id="description"
              className="bg-input text-foreground border border-border rounded p-2 w-full"
              placeholder="Enter description"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
            {errors.description && (
              <span className="text-xs text-red-600">{errors.description}</span>
            )}
          </div>
          <div>
            <label htmlFor="source" className="block text-sm font-medium mb-1">
              Source
            </label>
            <select
              id="source"
              className="bg-input text-foreground border border-border rounded p-2 w-full"
              value={source}
              onChange={e => setSource(e.target.value as any)}
            >
              <option value="manual">Manual</option>
              <option value="meta">Meta</option>
              <option value="clayrio">Clayrio</option>
            </select>
            {errors.source && (
              <span className="text-xs text-red-600">{errors.source}</span>
            )}
          </div>
          <div>
            <label htmlFor="channel" className="block text-sm font-medium mb-1">
              Channel
            </label>
            <select
              id="channel"
              className="bg-input text-foreground border border-border rounded p-2 w-full"
              value={channel}
              onChange={e => setChannel(e.target.value as any)}
              required
            >
              <option value="" disabled>
                Select channel...
              </option>
              <option value="ecommerce">Ecommerce</option>
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
            </select>
            {errors.channel && (
              <span className="text-xs text-red-600">{errors.channel}</span>
            )}
          </div>

          {channel === "retail" && (
            <div>
              <label htmlFor="location" className="block text-sm font-medium mb-1">
                Location
              </label>
              <input
                id="location"
                className="bg-input text-foreground border border-border rounded p-2 w-full"
                placeholder="Enter location"
                value={location}
                onChange={e => setLocation(e.target.value)}
                required
              />
              {errors.location && (
                <span className="text-xs text-red-600">{errors.location}</span>
              )}
            </div>
          )}
          <div>
            <label htmlFor="startAt" className="block text-sm font-medium mb-1">
              Start Date & Time
            </label>
            <input
              id="startAt"
              className="bg-input text-foreground border border-border rounded p-2 w-full"
              type="datetime-local"
              value={startAt}
              onChange={e => setStartAt(e.target.value)}
              required
            />
            {errors.startAt && (
              <span className="text-xs text-red-600">{errors.startAt}</span>
            )}
          </div>
          <div>
            <label htmlFor="endAt" className="block text-sm font-medium mb-1">
              End Date & Time
            </label>
            <input
              id="endAt"
              className="bg-input text-foreground border border-border rounded p-2 w-full"
              type="datetime-local"
              value={endAt}
              onChange={e => setEndAt(e.target.value)}
              required
            />
            {errors.endAt && (
              <span className="text-xs text-red-600">{errors.endAt}</span>
            )}
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              className="bg-muted text-muted-foreground border border-border rounded px-4 py-2"
              onClick={onCancel}
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={isAdding || isEditing}
              className="bg-primary text-primary-foreground border border-border rounded px-4 py-2"
            >
              {(isAdding || isEditing) ? (
                <>
                  <span className="animate-spin mr-2">⏳</span> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};